import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BarChart3, Loader2, FileText, FileSpreadsheet, FileType, FileCode, Download, Save, Trash2, Printer, Eye, Play, X, Archive, Sparkles } from "lucide-react";
import { toast } from "sonner";

const FORMAT_META = {
  xlsx: { label: "Excel (.xlsx)", icon: FileSpreadsheet },
  csv: { label: "CSV (.csv)", icon: FileSpreadsheet },
  pdf: { label: "PDF (.pdf)", icon: FileText },
  docx: { label: "Word (.docx)", icon: FileType },
  txt: { label: "Text (.txt)", icon: FileCode },
};
const FORMAT_ORDER = ["xlsx", "csv", "pdf", "docx", "txt"];
const STATUSES = ["all", "lead", "active", "inactive"];

export default function Reports() {
  const { user } = useAuth();
  const [schema, setSchema] = useState(null);
  const [type, setType] = useState("clients");
  const [columns, setColumns] = useState([]);
  const [selected, setSelected] = useState([]);
  const [format, setFormat] = useState("xlsx");
  const [tags, setTags] = useState([]);
  const [groups, setGroups] = useState([]);
  const [filters, setFilters] = useState({ status: "all", group_id: "", date_from: "", date_to: "", search: "", tags: [] });
  const [generating, setGenerating] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [presets, setPresets] = useState([]);
  const [archives, setArchives] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const loadTemplates = useCallback(() => api.get("/reports/templates").then((r) => setTemplates(r.data)).catch(() => {}), []);
  const loadArchives = useCallback(() => api.get("/reports/archives").then((r) => setArchives(r.data)).catch(() => {}), []);

  useEffect(() => {
    api.get("/reports/schema").then((r) => setSchema(r.data)).catch(() => {});
    api.get("/tags").then((r) => setTags(r.data)).catch(() => {});
    api.get("/groups").then((r) => setGroups(r.data)).catch(() => {});
    loadTemplates();
    loadArchives();
    api.get("/reports/presets").then((r) => setPresets(r.data)).catch(() => {});
  }, [loadTemplates, loadArchives]);

  const applyType = useCallback((t) => {
    setType(t);
    const cfg = schema?.types.find((x) => x.type === t);
    const cols = cfg?.columns || [];
    setColumns(cols);
    setSelected(cols.map((c) => c.key));
    setFilters({ status: "all", group_id: "", date_from: "", date_to: "", search: "", tags: [] });
  }, [schema]);

  useEffect(() => { if (schema) applyType("clients"); }, [schema, applyType]);

  const toggleCol = (key) =>
    setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  const toggleTag = (t) =>
    setFilters((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }));

  const buildFilters = () => ({
    status: filters.status, tags: filters.tags, group_id: filters.group_id || null,
    date_from: filters.date_from || null, date_to: filters.date_to || null,
    search: filters.search || null,
  });

  const downloadBlob = (data, filename) => {
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const generate = async () => {
    if (selected.length === 0) { toast.error("Select at least one column"); return; }
    setGenerating(true);
    try {
      const res = await api.post("/reports/generate",
        { type, format, columns: selected, filters: buildFilters() }, { responseType: "blob" });
      downloadBlob(res.data, `${type}_report.${format}`);
      toast.success(`${type} report generated (${format.toUpperCase()})`);
      loadArchives();
    } catch { toast.error("Report generation failed"); }
    finally { setGenerating(false); }
  };

  const saveTemplate = async () => {
    if (selected.length === 0) { toast.error("Select at least one column first"); return; }
    const name = window.prompt("Name this report template:");
    if (!name) return;
    try {
      await api.post("/reports/templates", { name, type, format, columns: selected, filters: buildFilters() });
      toast.success("Template saved");
      loadTemplates();
    } catch { toast.error("Could not save template"); }
  };

  const loadTemplate = (t) => {
    setType(t.type);
    const cfg = schema?.types.find((x) => x.type === t.type);
    setColumns(cfg?.columns || []);
    setSelected(t.columns?.length ? t.columns : (cfg?.columns || []).map((c) => c.key));
    setFormat(t.format);
    setFilters({
      status: t.filters?.status || "all", group_id: t.filters?.group_id || "",
      date_from: t.filters?.date_from || "", date_to: t.filters?.date_to || "",
      search: t.filters?.search || "", tags: t.filters?.tags || [],
    });
    toast.success(`Loaded "${t.name}"`);
  };

  const deleteTemplate = async (id) => {
    try { await api.delete(`/reports/templates/${id}`); loadTemplates(); toast.success("Template deleted"); }
    catch { toast.error("Could not delete template"); }
  };

  const applyPreset = (p) => loadTemplate(p);

  const runPreset = async (p) => {
    setGenerating(true);
    try {
      const res = await api.post("/reports/generate",
        { type: p.type, format: p.format, columns: p.columns, filters: p.filters }, { responseType: "blob" });
      downloadBlob(res.data, `${p.type}_report.${p.format}`);
      toast.success(`${p.name} generated`);
      loadArchives();
    } catch { toast.error("Report generation failed"); }
    finally { setGenerating(false); }
  };

  const openPreview = async () => {
    if (selected.length === 0) { toast.error("Select at least one column"); return; }
    setPreviewing(true);
    try {
      const res = await api.post("/reports/preview", { type, columns: selected, filters: buildFilters() });
      setPreview(res.data);
    } catch { toast.error("Could not load preview"); }
    finally { setPreviewing(false); }
  };

  const printPreview = () => {
    if (!preview) return;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print"); return; }
    const doc = w.document;
    doc.title = preview.title;
    const style = doc.createElement("style");
    style.textContent =
      "body{font-family:Arial,sans-serif;padding:24px;color:#111}" +
      "h1{color:#002FA7;font-size:20px;margin-bottom:2px}" +
      "p{color:#6b7280;font-size:12px;margin-top:0}" +
      "table{width:100%;border-collapse:collapse;margin-top:14px;font-size:12px}" +
      "th{background:#002FA7;color:#fff;text-align:left;padding:6px 8px}" +
      "td{border:1px solid #d1d5db;padding:6px 8px;vertical-align:top}" +
      "tr:nth-child(even) td{background:#f3f4f6}";
    doc.head.appendChild(style);

    const h1 = doc.createElement("h1");
    h1.textContent = preview.title;
    const meta = doc.createElement("p");
    meta.textContent = `${preview.count} records · generated ${new Date().toLocaleString()}`;
    doc.body.append(h1, meta);

    const table = doc.createElement("table");
    const thead = doc.createElement("thead");
    const htr = doc.createElement("tr");
    preview.headers.forEach((h) => {
      const th = doc.createElement("th");
      th.textContent = h;
      htr.appendChild(th);
    });
    thead.appendChild(htr);
    const tbody = doc.createElement("tbody");
    preview.rows.forEach((r) => {
      const tr = doc.createElement("tr");
      preview.keys.forEach((k) => {
        const td = doc.createElement("td");
        td.textContent = String(r[k] ?? "");
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
    table.append(thead, tbody);
    doc.body.appendChild(table);
    w.focus();
    w.print();
  };

  const downloadArchive = async (a) => {
    try {
      const res = await api.get(`/reports/archives/${a.id}/download`, { responseType: "blob" });
      downloadBlob(res.data, a.filename);
    } catch { toast.error("Archived file unavailable"); }
  };

  const deleteArchive = async (id) => {
    try { await api.delete(`/reports/archives/${id}`); loadArchives(); toast.success("Removed from archive"); }
    catch { toast.error("Could not delete"); }
  };

  const isClients = type === "clients";
  const labelCls = "block text-sm font-semibold text-gray-700 mb-1";
  const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent";

  return (
    <div className="p-8 max-w-5xl" data-testid="reports-page">
      <h1 className="font-heading text-3xl font-extrabold tracking-tight text-gray-900">Reports</h1>
      <p className="text-gray-500 text-sm mt-1.5 mb-7">Build and export reports across your data.</p>

      {/* Report type */}
      <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6">
        <label className={labelCls}>Report type</label>
        <div className="flex flex-wrap gap-2" data-testid="report-type-group">
          {(schema?.types || []).map((t) => (
            <button key={t.type} onClick={() => applyType(t.type)} data-testid={`report-type-${t.type}`}
              className={`px-4 py-2 rounded-sm text-sm font-semibold border transition-colors ${
                type === t.type ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Columns */}
      <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6">
        <div className="flex items-center justify-between mb-3">
          <label className={labelCls + " mb-0"}>Columns</label>
          <div className="text-xs">
            <button onClick={() => setSelected(columns.map((c) => c.key))} className="text-[#002FA7] font-semibold hover:underline mr-3" data-testid="select-all-columns">Select all</button>
            <button onClick={() => setSelected([])} className="text-gray-500 font-semibold hover:underline" data-testid="clear-columns">Clear</button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="report-columns">
          {columns.map((c) => (
            <label key={c.key} data-testid={`col-${c.key}`}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border cursor-pointer text-sm ${
                selected.includes(c.key) ? "bg-[#002FA7]/10 border-[#002FA7]/30 text-[#002FA7]" : "bg-white border-gray-200 text-gray-600"}`}>
              <input type="checkbox" checked={selected.includes(c.key)} onChange={() => toggleCol(c.key)} className="h-4 w-4 accent-[#002FA7]" />
              {c.label}
            </label>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6">
        <label className={labelCls + " mb-3"}>Filters</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {isClients && (
            <div>
              <label className="text-xs font-semibold text-gray-500">Status</label>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="filter-status" className={inputCls}>
                {STATUSES.map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : s}</option>)}
              </select>
            </div>
          )}
          {isClients && (
            <div>
              <label className="text-xs font-semibold text-gray-500">Group</label>
              <select value={filters.group_id} onChange={(e) => setFilters({ ...filters, group_id: e.target.value })} data-testid="filter-group" className={inputCls}>
                <option value="">All groups</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-gray-500">Created from</label>
            <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="filter-date-from" className={inputCls} />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500">Created to</label>
            <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="filter-date-to" className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-gray-500">Search</label>
            <input type="text" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="Keyword…" data-testid="filter-search" className={inputCls} />
          </div>
        </div>
        {isClients && tags.length > 0 && (
          <div className="mt-4">
            <label className="text-xs font-semibold text-gray-500">Tags</label>
            <div className="flex flex-wrap gap-2 mt-1.5" data-testid="filter-tags">
              {tags.map((t) => (
                <button key={t} onClick={() => toggleTag(t)} data-testid={`filter-tag-${t}`}
                  className={`px-2.5 py-1 rounded-sm text-xs font-semibold border ${
                    filters.tags.includes(t) ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-gray-600 border-gray-200"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Format + generate */}
      <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6">
        <label className={labelCls + " mb-3"}>Export format</label>
        <div className="flex flex-wrap gap-2 mb-5" data-testid="report-format-group">
          {FORMAT_ORDER.filter((f) => (schema?.formats || []).includes(f)).map((f) => {
            const Icon = FORMAT_META[f].icon;
            return (
              <button key={f} onClick={() => setFormat(f)} data-testid={`report-format-${f}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-semibold border transition-colors ${
                  format === f ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}>
                <Icon className="w-4 h-4" /> {FORMAT_META[f].label}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={generate} disabled={generating} data-testid="generate-report-button"
            className="bg-[#002FA7] hover:bg-[#002480] text-white px-5 py-2.5 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Generate &amp; download
          </button>
          <button onClick={openPreview} disabled={previewing} data-testid="preview-report-button"
            className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-5 py-2.5 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Preview &amp; print
          </button>
          <button onClick={saveTemplate} data-testid="save-template-button"
            className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-5 py-2.5 rounded-sm font-semibold text-sm flex items-center gap-2">
            <Save className="w-4 h-4" /> Save as template
          </button>
        </div>
      </div>

      {/* Quick presets */}
      {presets.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6" data-testid="presets-section">
          <h3 className="font-heading text-base font-bold text-gray-900 flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-[#002FA7]" /> Quick presets
          </h3>
          <p className="text-xs text-gray-500 mb-3">Ready-to-run reports with auto-updating date ranges.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {presets.map((p) => (
              <div key={p.key} data-testid={`preset-${p.key}`} className="border border-gray-100 rounded-sm p-3 flex items-start justify-between gap-3 hover:border-[#002FA7]/30 transition-colors">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{p.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{p.description}</p>
                  <p className="text-[11px] text-gray-400 mt-1">{p.type} · {p.format.toUpperCase()}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => runPreset(p)} disabled={generating} data-testid={`run-preset-${p.key}`} title="Generate now"
                    className="px-2.5 py-1.5 rounded-sm bg-[#002FA7] hover:bg-[#002480] text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-60">
                    <Download className="w-3.5 h-3.5" /> Run
                  </button>
                  <button onClick={() => applyPreset(p)} data-testid={`apply-preset-${p.key}`} title="Load into builder"
                    className="p-1.5 rounded-sm text-gray-500 hover:text-[#002FA7] hover:bg-blue-50"><Play className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Saved templates */}
      <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6" data-testid="templates-section">
        <h3 className="font-heading text-base font-bold text-gray-900 flex items-center gap-2 mb-3">
          <Save className="w-4 h-4 text-gray-400" /> Saved templates
        </h3>
        {templates.length === 0 ? (
          <p className="text-sm text-gray-400">No templates yet. Configure a report and click "Save as template".</p>
        ) : (
          <div className="space-y-2">
            {templates.map((t) => (
              <div key={t.id} data-testid={`template-${t.id}`} className="flex items-center justify-between border border-gray-100 rounded-sm px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{t.name}</p>
                  <p className="text-xs text-gray-500">{t.type} · {t.format.toUpperCase()} · {t.columns?.length || "all"} cols</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => loadTemplate(t)} data-testid={`load-template-${t.id}`} title="Load into builder" className="p-1.5 rounded-sm text-gray-500 hover:text-[#002FA7] hover:bg-blue-50"><Play className="w-4 h-4" /></button>
                  <button onClick={() => deleteTemplate(t.id)} data-testid={`delete-template-${t.id}`} title="Delete" className="p-1.5 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Archive */}
      <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6" data-testid="archive-section">
        <h3 className="font-heading text-base font-bold text-gray-900 flex items-center gap-2 mb-3">
          <Archive className="w-4 h-4 text-gray-400" /> Report archive
        </h3>
        {archives.length === 0 ? (
          <p className="text-sm text-gray-400">Generated reports are saved here for re-download.</p>
        ) : (
          <div className="space-y-2">
            {archives.map((a) => (
              <div key={a.id} data-testid={`archive-${a.id}`} className="flex items-center justify-between border border-gray-100 rounded-sm px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{a.filename}</p>
                  <p className="text-xs text-gray-500">{a.row_count} rows · {(a.size / 1024).toFixed(1)} KB · {a.created_at?.slice(0, 16).replace("T", " ")}{a.owner_name ? ` · ${a.owner_name}` : ""}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => downloadArchive(a)} disabled={!a.available} data-testid={`download-archive-${a.id}`} title={a.available ? "Download" : "File unavailable"} className="p-1.5 rounded-sm text-gray-500 hover:text-[#002FA7] hover:bg-blue-50 disabled:opacity-40"><Download className="w-4 h-4" /></button>
                  <button onClick={() => deleteArchive(a.id)} data-testid={`delete-archive-${a.id}`} title="Delete" className="p-1.5 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-6" data-testid="preview-modal" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-sm shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <div>
                <h3 className="font-heading font-bold text-gray-900">{preview.title}</h3>
                <p className="text-xs text-gray-500">{preview.count} records</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={printPreview} data-testid="print-report-button" className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2"><Printer className="w-4 h-4" /> Print</button>
                <button onClick={() => setPreview(null)} data-testid="close-preview-button" className="p-2 rounded-sm text-gray-500 hover:bg-gray-100"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="overflow-auto p-5">
              <table className="w-full text-left text-sm border-collapse">
                <thead className="sticky top-0">
                  <tr>{preview.headers.map((h) => <th key={h} className="bg-[#002FA7] text-white font-semibold px-3 py-2 whitespace-nowrap">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {preview.rows.length === 0 ? (
                    <tr><td colSpan={preview.headers.length} className="text-center text-gray-400 py-8">No matching records</td></tr>
                  ) : preview.rows.map((r, i) => (
                    <tr key={`${preview.keys.map((k) => r[k]).join("|")}-${i}`} className="even:bg-gray-50">
                      {preview.keys.map((k) => <td key={k} className="border border-gray-200 px-3 py-1.5 text-gray-700 align-top">{String(r[k] ?? "")}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
